// Section "Orchestration" d'un environnement : frise (Gantt) d3 des jobs executes sur 1 a 7 jours,
// dans l'ordre d'ordonnancement, puis tableau. L'appel n'a lieu qu'avec un environnement selectionne.
import * as d3 from "d3";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin, Table, Tag } from "antd";

import axios from "../../utils/axios";
import useResize from "../../hooks/useResize";
import { COULEURS_CATEGORIES, categorieFlux } from "../Logs/categoriesFlux";
import { proprietesPagination } from "./cartographieDonnees";
import {
  BarreFiltres,
  libelleDepuis,
  libelleJusqua,
  usePeriode,
  GOUTTIERE,
  StatutRun,
  libelleStale,
  libelleStaleNote,
  nbStale,
  definirHachures,
  dessinerAxe,
  dessinerBarres,
  dessinerGrille,
  useOuvrirLogs,
} from "./FriseCommune";
import { clePeriode, nbJoursFenetre, paramsApi, premierJour, titrePlage } from "./periode";
import { PLAGE_DEFAUT, construireEchelle, finEffective, intersecteTranches, ms, tranchesHoraires } from "./frise";

const LIGNE_H = 18;
const ID_HACHURES = "orch-hachures";
const liste = (v) => (Array.isArray(v) ? v : []);

export const trierRuns = (runs) => [...liste(runs)].sort((a, b) => (a.order || 0) - (b.order || 0));

export const formaterDebutFin = (v) => (v ? moment(String(v)).format("DD/MM HH:mm:ss") : "—");

export function formaterDureeS(s) {
  if (s === null || s === undefined || isNaN(Number(s))) return "—";
  const n = Math.round(Number(s));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const sec = n % 60;
  return h ? `${h} h ${String(m).padStart(2, "0")} min` : m ? `${m} min ${String(sec).padStart(2, "0")} s` : `${sec} s`;
}

// Ordres a mettre en evidence pour un run survole : lui-meme et ses chevauchements.
export const ordresLies = (runs, ordre) => {
  if (ordre === null || ordre === undefined) return new Set();
  const r = liste(runs).find((x) => x.order === ordre);
  return new Set([ordre, ...liste(r && r.overlaps)]);
};

export default function Orchestration({ envId, plage: plageProp, onPlage }) {
  const [periode, { setDays, setRange }] = usePeriode();
  const clePer = clePeriode(periode);
  const [oldest, setOldest] = useState(null);
  const [plageLocale, setPlageLocale] = useState(PLAGE_DEFAUT);
  const plage = plageProp || plageLocale;
  const majPlage = onPlage || setPlageLocale;
  const [etat, setEtat] = useState({ chargement: false, erreur: false, data: null });
  const [survol, setSurvol] = useState(null);
  const [page, setPage] = useState({ current: 1, pageSize: 50 });
  const conteneur = useRef();
  const svgRef = useRef();
  const axeRef = useRef();
  const size = useResize(conteneur);
  const ouvrirLogs = useOuvrirLogs();
  const largeur = (size && size.width) || 900;

  useEffect(() => {
    if (envId === null || envId === undefined) return undefined;
    let actif = true; // l'env ou la periode a change : la reponse perimee est ignoree
    setEtat({ chargement: true, erreur: false, data: null });
    setSurvol(null);
    setPage((p) => ({ ...p, current: 1 }));
    axios
      .get(`/api/supervision/environments/${envId}/runs`, { params: paramsApi(periode) })
      .then((res) => {
        if (!actif) return;
        setOldest(premierJour(res && res.data && res.data.oldest_available) || null);
        setEtat({ chargement: false, erreur: false, data: (res && res.data) || {} });
      })
      .catch(() => actif && setEtat({ chargement: false, erreur: true, data: null }));
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, clePer]);

  useEffect(() => {
    setPage((p) => ({ ...p, current: 1 }));
  }, [etat.data, plage]);

  const tous = useMemo(() => trierRuns(etat.data && etat.data.runs), [etat.data]);
  const tranches = useMemo(
    () => (etat.data ? tranchesHoraires(etat.data.from, etat.data.to, plage) : []),
    [etat.data, plage]
  );
  // Filtre d'heures : un run n'est conserve que s'il intersecte une tranche retenue.
  const runs = useMemo(() => {
    const fin = ms(etat.data && etat.data.to);
    return tous.filter((r) => intersecteTranches(tranches, ms(r.start), finEffective(r, fin)));
  }, [tous, tranches, etat.data]);
  const lies = useMemo(() => ordresLies(runs, survol), [runs, survol]);

  const ouvrirRef = useRef(ouvrirLogs);
  ouvrirRef.current = ouvrirLogs;

  // Frise : une ligne par run (ordre d'ordonnancement), axe casse selon le filtre d'heures.
  useEffect(() => {
    if (!svgRef.current || !axeRef.current || !etat.data || runs.length === 0) return;
    const w = Math.max(300, largeur - GOUTTIERE - 16);
    const ech = construireEchelle(tranches, GOUTTIERE, w);
    const fin = ms(etat.data.to);
    const hauteur = runs.length * LIGNE_H + 4;
    dessinerAxe(d3.select(axeRef.current), ech, nbJoursFenetre(periode, etat.data), largeur, plage.origine || 0);
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", largeur).attr("height", hauteur);
    definirHachures(svg, ID_HACHURES);
    dessinerGrille(svg, ech, nbJoursFenetre(periode, etat.data), hauteur, plage.origine || 0);
    const lignes = svg
      .append("g")
      .selectAll("g.run")
      .data(runs)
      .join("g")
      .attr("class", "run")
      .attr("data-order", (r) => r.order)
      .attr("transform", (r, i) => `translate(0,${2 + i * LIGNE_H})`)
      .style("cursor", "pointer")
      .on("mouseenter", (ev, r) => setSurvol(r.order))
      .on("mouseleave", () => setSurvol(null))
      .on("click", (ev, r) => ouvrirRef.current(r));
    lignes.append("rect").attr("x", 0).attr("y", 0).attr("width", largeur).attr("height", LIGNE_H).attr("fill", "transparent");
    lignes
      .append("text")
      .attr("x", 4)
      .attr("y", LIGNE_H / 2)
      .attr("dy", "0.35em")
      .attr("font-size", 11)
      .attr("fill", "#1d2733")
      .text((r) => {
        const t = `${r.order}. ${r.label || r.filename}`;
        return t.length > 34 ? `${t.slice(0, 33)}…` : t;
      });
    lignes.each(function (r) {
      dessinerBarres(d3.select(this), ech.segments(ms(r.start), finEffective(r, fin)), {
        couleur: COULEURS_CATEGORIES[categorieFlux(r.filename)] || COULEURS_CATEGORIES.AUTRES,
        statut: r.status,
        jusqua: libelleJusqua(r.end ? ms(r.end) : NaN, ms(etat.data.to)),
        depuis: libelleDepuis(ms(r.start), ms(etat.data.from)),
        stale: r.stale,
        libelleStale: r.stale ? libelleStale(r) : null,
        y: 3,
        h: LIGNE_H - 6,
        idHachures: ID_HACHURES,
        titre: `${r.filename} — ${formaterDebutFin(r.start)} → ${r.end ? formaterDebutFin(r.end) : r.status === "running" ? "en cours" : "—"} (${formaterDureeS(r.duration_s)})`,
      });
    });
  }, [runs, tranches, etat.data, largeur, clePer, plage]);

  // Survol : le run et ses chevauchements restent opaques, les autres sont estompes.
  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current)
      .selectAll("g.run")
      .attr("class", (r) => `run${survol === null ? "" : lies.has(r.order) ? " lie" : " estompe"}`)
      .attr("opacity", (r) => (survol === null || lies.has(r.order) ? 1 : 0.25));
  }, [survol, lies, runs]);

  const colonnes = [
    { title: "N°", dataIndex: "order", key: "order", width: 60 },
    {
      title: "Job",
      key: "job",
      render: (_, r) => (
        <span>
          {r.label || r.filename}
          <div className="carto-sub">{r.filename}</div>
        </span>
      ),
    },
    {
      title: "Catégorie",
      key: "categorie",
      render: (_, r) => {
        const c = categorieFlux(r.filename);
        return <Tag color={COULEURS_CATEGORIES[c]}>{c}</Tag>;
      },
    },
    { title: "Prestataire", dataIndex: "provider", key: "provider", render: (p) => p || "—" },
    { title: "Début", dataIndex: "start", key: "start", render: formaterDebutFin },
    { title: "Fin", dataIndex: "end", key: "end", render: (v, r) => (v ? formaterDebutFin(v) : r.status === "running" ? "en cours" : "—") },
    { title: "Durée", dataIndex: "duration_s", key: "duree", render: formaterDureeS },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (s, r) => <StatutRun run={r} />,
    },
    {
      title: "Chevauche n°",
      key: "overlaps",
      render: (_, r) => (liste(r.overlaps).length ? liste(r.overlaps).join(", ") : "—"),
    },
  ];

  const nbJours = nbJoursFenetre(periode, etat.data);
  return (
    <div className="carto-orchestration" data-testid="orchestration" ref={conteneur}>
      <div className="carto-orch-tete">
        <h3>
          {periode.mode === "range"
            ? `Orchestration — ${titrePlage(periode.from, periode.to)}`
            : `Orchestration — ${nbJours} ${nbJours > 1 ? "derniers jours" : "dernier jour"}`}
        </h3>
        <BarreFiltres periode={periode} onDays={setDays} onRange={setRange} oldest={oldest} plage={plage} onPlage={majPlage} />
      </div>
      {nbStale(runs) > 0 && (
        <div className="orch-stale-note" data-testid="stale-note">
          ⚠ {libelleStaleNote(nbStale(runs))} (bloqué ?), non pris en compte dans les chevauchements
        </div>
      )}
      {etat.chargement && (
        <div className="carto-vide">
          <Spin />
        </div>
      )}
      {etat.erreur && (
        <Alert type="error" showIcon message="Impossible de charger l'orchestration de cet environnement" />
      )}
      {!etat.chargement && !etat.erreur && etat.data && tous.length === 0 && (
        <div className="carto-vide">aucun job exécuté sur la période</div>
      )}
      {!etat.chargement && !etat.erreur && etat.data && tous.length > 0 && runs.length === 0 && (
        <div className="carto-vide">aucun job sur cette plage horaire</div>
      )}
      {runs.length > 0 && (
        <>
          {etat.data.truncated && (
            <Alert type="warning" showIcon message="Liste tronquée : seuls les 2000 jobs les plus récents sont affichés" />
          )}
          <div className="carto-frise">
            <svg ref={axeRef} className="orch-axe" aria-hidden="true" />
            <svg ref={svgRef} role="img" aria-label="Frise d'orchestration" />
          </div>
          <Table
            size="small"
            rowKey="order"
            columns={colonnes}
            dataSource={runs}
            pagination={proprietesPagination({ page, total: runs.length, unite: "jobs", onChange: setPage })}
            onRow={(r) => ({
              onMouseEnter: () => setSurvol(r.order),
              onMouseLeave: () => setSurvol(null),
              onClick: () => ouvrirLogs(r),
              className: survol === null ? "" : lies.has(r.order) ? "orch-lie" : "orch-estompe",
              style: { cursor: "pointer" },
            })}
          />
        </>
      )}
    </div>
  );
}
